import { useEffect, useRef, useState, type ReactElement } from "react";
import { PTable } from "../../../../shared/layout/PTable";
import {
  Drawer,
  Flex,
  Select,
  Radio,
  Space,
  type RadioChangeEvent,
} from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { Search } from "../../../../shared/layout/Search";
import boxIcon from "../../../../../src/assets/images/product-search-icon.svg";
import { useCreateProtocolForm } from "../..";
import { measureUnitOptions } from "../../constants";
import { useSearchParams } from "react-router-dom";
import AxiosInstance from "../../../../shared/utils/axios";
import PlatFormAPIAxiosInstance from "../../../../shared/utils/platFormAxios";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { DensityRangeComponent } from "../../shared/components/DensityRangeComponent";
import { ProductCard } from "../../shared/components/ProductCard";
import { transformProductList } from "../../shared/utils/common-utils";
import type { ProtocolResponse } from "../../shared/interfaces/product-interfaces";
import usePagination from "../../../../shared/custom-hooks/usePagination";
import { useProtocolsRouter } from "../../../../shared/custom-hooks/useProtocolsRouter";

interface Product {
  // React.Key (string | number), not just number: rows are keyed by their
  // real catalog/product id (a UUID string) whenever one exists, to avoid
  // colliding with the small sequential integer keys catalog rows get from
  // `transformProductList`.
  key: React.Key;
  id: string;
  variety: string;
  manufacturer: string;
  seed_care: string | null;
  description: string | null;
  measure_unit: string;
  density_range?: {
    max?: number;
    min?: number;
  };
  density_options?: number[];
}

type DensityMode = "range" | "options";

// Normalize a product (variety) name for cross-country matching:
// treat underscores/hyphens as spaces (naming conventions vary by source,
// e.g. "SY_ZOOMBA" vs "SY Zoomba"), collapse whitespace, lowercase. Prevents
// false drops caused by cosmetic differences ("SY KingsBarn " vs "sy
// kingsbarn", "SY_ZOOMBA" vs "SY Zoomba").
const normalizeName = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

// Stable identity for a product row when `id` may be missing (persisted
// products with no catalog match). Used to dedupe the catalog against the
// "known but uncatalogued" pool below.
const productIdentity = (p: any): string => String(p?.id ?? p?.variety ?? "");

const ProductsSeedComponent = (): ReactElement => {
  const { t } = useTranslation();
  const { form, setFormValidationData, formValidationData } =
    useCreateProtocolForm();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  // Additive-only pool of products that were selected at some point but have
  // no entry in the fetched catalog (an empty/incomplete catalog fetch, or a
  // product persisted directly onto the protocol with no browsable catalog
  // row). Without this, deselecting one of these products makes it vanish
  // from the left table entirely instead of remaining as an unchecked,
  // reselectable row -- there is nothing else to source that row from once
  // it drops out of `selectedProducts`.
  const [extraProducts, setExtraProducts] = useState<Product[]>([]);
  const [productList, setProductList] = useState<any>([]);
  const [globalDensityRange, setGlobalDensityRange] = useState<boolean>(false);

  const [globalDensityMode, setGlobalDensityMode] =
    useState<DensityMode>("range");

  const [globalDensityValues, setGlobalDensityValues] = useState<{
    min: number | null;
    max: number | null;
  }>({
    min: null,
    max: null,
  });

  const [globalDensityOptions, setGlobalDensityOptions] = useState<number[]>(
    []
  );

  const [measureUnitSelection, setMeasureUnitSelection] =
    useState<string>("seeds/m2");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setloading] = useState(true);
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get("protocol_id");
  const isFromTemplate = Boolean(searchParams.get("template_id"));
  const [existingData, setExistingData] = useState<any>(null);
  let formattedProducts: Product[] = [];
  const { workspaceId, orgId } = useProtocolsRouter();

  // #2: True only between "we kicked off a revalidation catalog fetch" and
  // "that fetch has resolved and been processed". Guarantees the revalidation
  // result effect can never run against a stale (initial/old-country) catalog.
  const revalidationFetchPendingRef = useRef<boolean>(false);

  // #1 (race fix): monotonically increasing id for every catalog fetch. Only
  // the response from the LATEST fetch is allowed to update `productList`.
  // Without this, the initial (old-country) fetch and the revalidation
  // (new-country) fetch can resolve out of order, leaving a stale catalog that
  // the matching then runs against.
  const latestCatalogReqRef = useRef<number>(0);

  // Scenario B sentinel: set the moment a template-driven country change tells
  // us the crop no longer exists in the new country. The protocol data fetched
  // by getProtocolData() still carries the OLD crop's seed selections (they are
  // only wiped on save), and that fetch resolves AFTER this component mounts.
  // Without this ref, the async `existingData` restore below would re-add the
  // very products we just cleared. While this ref is true, we skip restoring
  // any previously persisted seed selections.
  const seedCountryChangeClearRef = useRef<boolean>(false);

  const getProtocolData = async (): Promise<any> => {
    try {
      const response = await AxiosInstance.get(
        `/protocol/${protocolId as string}`,
        {
          headers: {
            "org-id": orgId ?? "",
            "workspace-id": workspaceId ?? "",
          },
        }
      );
      setExistingData(response);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (protocolId) {
      getProtocolData().catch((error) => {
        console.error("Error fetching data:", error);
      });
    }
  }, [protocolId]);

  const getProductList = async (localizedCropIds: string[]): Promise<void> => {
    // #1: claim a request id BEFORE awaiting. Any earlier in-flight request
    // becomes stale the moment a newer one starts.
    const reqId = ++latestCatalogReqRef.current;

    const payload = {
      localized_crop_id: localizedCropIds,
      show_canonical: true,
    };

    const response: ProtocolResponse = await PlatFormAPIAxiosInstance.post(
      "/catalog/seeds/query?page=1&size=999",
      payload
    );

    // #1: drop out-of-order responses; only the latest fetch wins.
    if (reqId !== latestCatalogReqRef.current) {
      return;
    }

    setProductList(response?.data);
    setloading(false);
  };

  useEffect(() => {
    // Genuinely ACTIVE right now -- some other effect is already fetching a
    // catalog and will own the keep-vs-clear decision for it. Deliberately
    // excludes `revalidationCountry`, which stays truthy for the rest of the
    // session after any template country/crop change even once its revalidation
    // has long since settled -- treating that stale flag the same as a live
    // fetch here would (and did) permanently skip the re-arm block below on
    // every later remount, so a freshly persisted selection (e.g. after
    // picking a new crop's product and coming back from Summary) could never
    // be re-fetched/restored again.
    const revalidationFetchActive = Boolean(
      formValidationData?.seedRevalidationRequired ||
        formValidationData?.seedRevalidationProcessing ||
        formValidationData?.countryChangeSeedsPending
    );

    if (existingData?.data) {
      // A country change is already revalidating against the NEW country's
      // catalog (see the seedRevalidationRequired/Processing effects below,
      // which fetch using revalidationLocalizedCropIds). That fetch starts on
      // mount, before this component's own protocol fetch resolves. If we ALSO
      // fetch the OLD (persisted) crop's catalog here, this later call claims
      // a higher request id and "wins" per latestCatalogReqRef, clobbering
      // `productList` with the wrong (old-country) catalog right as the
      // reconcile effect is about to match against it -- which is exactly why
      // a product that no longer exists in the new country can still appear
      // to "match" and never gets cleared. Skip the old-catalog fetch here
      // while a revalidation is in flight; the revalidation effect owns it.
      // `revalidationCountry` also counts as "owned by the country-change
      // pathway" for this direct-fetch decision specifically -- once any
      // template-driven country/crop change has been processed this session,
      // never trust `existingData.crop_details.localized_crop_ids` directly
      // again; the re-arm block below re-derives it from the CURRENT form
      // crop id instead, every time.
      //
      // Gated to the template flow specifically: `revalidationCountry` is
      // only ever written inside `isFromTemplate` blocks in
      // BasicInfoComponent.tsx, and formValidationData is shared, unscoped
      // wizard state that outlives a single protocol's session -- once it's
      // set (even by an earlier, unrelated template protocol tested in the
      // same browser tab) nothing ever clears it back to falsy for a plain
      // protocol. Without this `isFromTemplate` check, that stale value
      // permanently disabled this direct fetch for every later non-template
      // protocol's Products screen, on every visit, regardless of which
      // country was actually selected -- confirmed live: a normal (non-
      // template) protocol's second country change left `revalidationCountry`
      // stuck at a prior session's value and `getProductList` was never
      // called again, stranding the Seeds tab on its loading spinner forever.
      const skipDirectFetch =
        revalidationFetchActive ||
        (isFromTemplate && Boolean(formValidationData?.revalidationCountry));

      if (!skipDirectFetch) {
        getProductList(
          existingData?.data?.crop_details?.localized_crop_ids
        ).catch((e) => {
          console.error(e);
          // `getProductList` is the only place that ever clears `loading` on
          // success (see its own body) -- a rejection here (bad/empty
          // localized_crop_ids after a plain, non-template country change
          // that isn't wired into any revalidation flag, a network error,
          // etc.) previously left nothing to clear it, stranding the Products
          // screen on its spinner forever instead of showing an empty/error
          // state.
          setloading(false);
        });

        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          products: [],
        }));
      }
    }

    // Scenario B: a country change removed the crop. Do NOT restore the
    // previously persisted seed selections; they have already been cleared
    // by the clear/revalidation effect below.
    if (seedCountryChangeClearRef.current) {
      return;
    }

    // Some OTHER effect (the country-only `countryChangeSeedsPending` flow, or
    // an already-armed seedRevalidationRequired/Processing cycle) is actively
    // reconciling this exact catalog right now -- let it own the keep-vs-clear
    // decision instead of racing it with a second, redundant arm here.
    if (revalidationFetchActive) {
      return;
    }

    if (existingData?.data?.product_details?.seeds) {
      const seeds = existingData?.data?.product_details?.seeds;

      // Never trust a persisted seed selection as-is in the template flow --
      // always re-run it through the same name-matching revalidation a live
      // country change uses, rather than only when a country mismatch is
      // detected. A draft can be auto-created from a template with the
      // template's original products attached while the country field was
      // already changed, with no backend-side validation ever having run --
      // so `persisted country === current country` does NOT guarantee the
      // persisted products are actually valid for that country. This also
      // covers the remount case (`seedCountryChangeClearRef` above resets on
      // every mount, so it can't durably remember "already found stale") --
      // re-deriving here (rather than trusting `revalidationCountry` to still
      // mean "in flight") is what makes a freshly saved selection for a newly
      // picked crop survive a Products -> Summary -> Products round trip.
      //
      // `localized_crop_id` is a DIFFERENT id space than the crop's plain
      // `crop_id` (see the identical note in BasicInfoComponent.tsx's
      // `applyTemplateCountryChange`) -- the seed catalog query only
      // understands the localized id. `existingData.crop_details` is the
      // backend's OWN freshly-resolved localization for this exact protocol's
      // current country+crop, so it's the correct source here; falling back
      // to `form.getFieldValue("protocolCropId")` (the plain, non-localized
      // crop id) fed the wrong id space into the catalog query and made every
      // persisted product look mismatched -- even on a brand new,
      // never-touched template protocol -- clearing a valid selection to
      // empty for no real reason.
      //
      // BUT: confirmed via live debug logging that `existingData`'s own
      // `localized_crop_ids` can be a DIFFERENT value than the one the
      // country-change flow (BasicInfoComponent's `/template/protocol/{id}
      // /data?country_code=`) just resolved for the SAME country+crop --
      // both are valid catalog ids and both return real (but DIFFERENT --
      // different products AND different ids for same-named products)
      // result sets. Re-deriving from `existingData` here after a country
      // change already resolved a value THIS session re-fetches the catalog
      // under a second, inconsistent id, remapping the selection onto a
      // different underlying product record than the one just shown --
      // which is what produced a same-named row appearing twice (two
      // catalogs, two different ids, no way to dedupe them) and density
      // data flip-flopping. Once a country-change revalidation has already
      // resolved a localized crop id this session, that is the freshest and
      // authoritative one -- keep using it instead of re-deriving.
      const sessionRevalidationCropIds = Array.isArray(
        formValidationData?.revalidationLocalizedCropIds
      )
        ? formValidationData.revalidationLocalizedCropIds
        : [];

      const currentLocalizedCropIds =
        sessionRevalidationCropIds.length > 0
          ? sessionRevalidationCropIds
          : Array.isArray(existingData?.data?.crop_details?.localized_crop_ids)
          ? existingData.data.crop_details.localized_crop_ids
          : (() => {
              const currentCropId = form.getFieldValue("protocolCropId");
              return currentCropId ? [currentCropId] : [];
            })();

      if (isFromTemplate) {
        if (currentLocalizedCropIds.length > 0) {
          setFormValidationData((prev: any) => ({
            ...(prev ?? {}),
            seedRevalidationRequired: true,
            revalidationLocalizedCropIds: currentLocalizedCropIds,
          }));
        } else {
          clearAllSeedSelections();
        }
        return;
      }

      setSelectedProducts(seeds?.products);
      setGlobalDensityRange(seeds?.global_density_range);

      setGlobalDensityValues({
        min: seeds?.density_range?.min ?? null,
        max: seeds?.density_range?.max ?? null,
      });

      setMeasureUnitSelection(seeds?.measure_unit);

      const hasOptions =
        (Array.isArray(seeds?.density_options) &&
          seeds.density_options.length) > 0 ||
        (Array.isArray(seeds?.products) &&
          seeds.products.some(
            (p: any) =>
              Array.isArray(p?.density_options) && p.density_options.length > 0
          ));

      const initialMode: DensityMode = hasOptions ? "options" : "range";
      setGlobalDensityMode(initialMode);

      setGlobalDensityOptions(
        Array.isArray(seeds?.density_options) ? seeds.density_options : []
      );

      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        measure_unit: seeds?.measure_unit,
        products: seeds?.products,
        density_range: seeds?.density_range,
        globalDensityRange: seeds?.global_density_range,
        globalDensityMode: initialMode,
        globalDensityOptions: Array.isArray(seeds?.density_options)
          ? seeds.density_options
          : [],
      }));
    }
  }, [existingData, protocolId]);

  if (productList?.content && productList?.content.length > 0) {
    formattedProducts = transformProductList(productList?.content);
  } else {
    formattedProducts = [];
  }

  // Clears ALL selected seed products and their density calculations. Used when
  // a template-driven country change removes the crop (Scenario B) or when NO
  // selected product survives in the new country (per-product result: zero
  // survivors).
  const clearAllSeedSelections = (): void => {
    setSelectedProducts([]);
    setSelectedRowKeys([]);
    // The uncatalogued-product fallback pool is scoped to whatever crop it
    // was built under -- it must not survive into a different crop's browse
    // list, or a product that no longer belongs to the selected crop (e.g.
    // the old crop's product persisted on the draft) keeps appearing as a
    // selectable row forever, since this pool is otherwise additive-only.
    setExtraProducts([]);
    setGlobalDensityRange(false);
    setGlobalDensityMode("range");
    setGlobalDensityValues({ min: null, max: null });
    setGlobalDensityOptions([]);

    form.setFieldsValue({
      seed_details: {
        global_density_range: false,
        measure_unit: measureUnitSelection,
        products: [],
        density_range: null,
        density_options: null,
      },
    });

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      products: [],
      density_range: null,
      density_options: null,
      globalDensityRange: false,
      globalDensityMode: "range",
      globalDensityOptions: [],
      measure_unit: measureUnitSelection,
    }));
  };

  // Restores the ORIGINAL (template-default) seed selections from the protocol
  // data fetched on mount. Used when the user returns to the original country.
  // Re-fetches the original country's catalog so product ids/keys match.
  const restoreSeedSelectionsFromExisting = (): void => {
    const seeds = existingData?.data?.product_details?.seeds;
    if (!seeds) {
      clearAllSeedSelections();
      return;
    }

    getProductList(existingData?.data?.crop_details?.localized_crop_ids).catch(
      (e) => {
        console.error(e);
      }
    );

    setSelectedProducts(seeds?.products);
    setGlobalDensityRange(seeds?.global_density_range);
    setGlobalDensityValues({
      min: seeds?.density_range?.min ?? null,
      max: seeds?.density_range?.max ?? null,
    });
    setMeasureUnitSelection(seeds?.measure_unit);

    const hasOptions =
      (Array.isArray(seeds?.density_options) && seeds.density_options.length) >
        0 ||
      (Array.isArray(seeds?.products) &&
        seeds.products.some(
          (p: any) =>
            Array.isArray(p?.density_options) && p.density_options.length > 0
        ));
    const initialMode: DensityMode = hasOptions ? "options" : "range";
    setGlobalDensityMode(initialMode);
    setGlobalDensityOptions(
      Array.isArray(seeds?.density_options) ? seeds.density_options : []
    );

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      measure_unit: seeds?.measure_unit,
      products: seeds?.products,
      density_range: seeds?.density_range,
      globalDensityRange: seeds?.global_density_range,
      globalDensityMode: initialMode,
      globalDensityOptions: Array.isArray(seeds?.density_options)
        ? seeds.density_options
        : [],
    }));
  };

  // Returning to the original country -> restore the original seed selections
  // and allow future Scenario B clears again.
  useEffect(() => {
    if (formValidationData?.restoreOriginalSeeds) {
      revalidationFetchPendingRef.current = false;
      seedCountryChangeClearRef.current = false;
      restoreSeedSelectionsFromExisting();
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        restoreOriginalSeeds: false,
      }));
    }
  }, [formValidationData?.restoreOriginalSeeds]);

  // Country-change coordination (template flow only):
  // - clearProductsAndCalculations -> crop removed (Scenario B): wipe all seeds.
  // - seedRevalidationRequired     -> crop matched (Scenario A): re-fetch the
  //   new country's catalog. The actual keep/drop decision is made in the
  //   revalidation-result effect once the new catalog has loaded.
  useEffect(() => {
    if (formValidationData?.clearProductsAndCalculations) {
      revalidationFetchPendingRef.current = false;
      // Latch Scenario B so the async `existingData` restore above cannot
      // re-add the old crop's seed selections once the protocol fetch resolves.
      seedCountryChangeClearRef.current = true;
      clearAllSeedSelections();
      // There's no crop to fetch a catalog for, so nothing else will ever
      // call getProductList (the only other place that clears `loading`).
      // Without this, the table spins forever instead of showing empty.
      setloading(false);
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        clearProductsAndCalculations: false,
        seedRevalidationRequired: false,
        seedRevalidationProcessing: false,
      }));
      return;
    }

    if (formValidationData?.seedRevalidationRequired) {
      const localizedCropIds =
        formValidationData?.revalidationLocalizedCropIds ?? [];

      // #2: mark the upcoming fetch as the revalidation fetch BEFORE it starts.
      // The result effect will only act once this fetch has resolved.
      revalidationFetchPendingRef.current = true;

      // A crop change invalidates any previously accumulated uncatalogued
      // pool -- it was built against the OLD crop's catalog, so an entry left
      // in it (e.g. a product that didn't survive the name-match below) would
      // otherwise keep showing up as a browsable row under the NEW crop too.
      setExtraProducts([]);

      setloading(true);
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        seedRevalidationRequired: false,
        seedRevalidationProcessing: true,
      }));

      getProductList(localizedCropIds).catch((e) => {
        console.error(e);
        // Fetch failed: don't strand the flow in a processing (spinning
        // forever) state -- getProductList is the only place that ever
        // clears `loading`, so if it throws before reaching that line,
        // nothing else will.
        revalidationFetchPendingRef.current = false;
        setloading(false);
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          seedRevalidationProcessing: false,
        }));
      });
    }
  }, [
    formValidationData?.clearProductsAndCalculations,
    formValidationData?.seedRevalidationRequired,
  ]);

  // Country change (template flow), backend-resolved result: BasicInfoComponent
  // already asked the template-by-country endpoint to match the currently
  // selected products against the new country's catalog. A mismatched product
  // comes back with `id`/`variety` nulled (array length unchanged) -- that IS
  // the mismatch signal, no separate catalog fetch + name-match needed here.
  useEffect(() => {
    if (!formValidationData?.countryChangeSeedsPending) return;

    revalidationFetchPendingRef.current = false;

    // Same reasoning as the seedRevalidationRequired arm: a country/crop
    // change invalidates the OLD crop's uncatalogued-product pool.
    setExtraProducts([]);

    const seeds = formValidationData?.countryChangeSeeds;
    const rawProducts = Array.isArray(seeds?.products) ? seeds.products : [];
    const matchedProducts = rawProducts.filter((p: any) => p?.id && p?.variety);
    // Keep whichever products the backend matched; drop only the ones it
    // came back with nulled id/variety for (individually mismatched), not
    // the whole selection.
    const anySurvive = matchedProducts.length > 0;

    let remapped: Product[] = [];

    if (anySurvive) {
      seedCountryChangeClearRef.current = false;

      // This endpoint resolves the TEMPLATE's own default product list against
      // the new country's catalog -- it knows nothing about density/rate
      // values the user already customized on THIS draft protocol, so
      // `p.density_range`/`p.density_options` here are the template's
      // defaults (usually absent), not the draft's saved values. Preserve the
      // draft's own density data by matching the still-current (pre-country-
      // change) `selectedProducts` by normalized variety name, same as the
      // frontend name-matching revalidation path below does via `...selected`.
      const previousByName = new Map<string, Product>(
        selectedProducts.map((sp) => [normalizeName(sp.variety), sp])
      );

      remapped = matchedProducts.map((p: any, index: number) => {
        const previous = previousByName.get(normalizeName(p.variety));

        return {
          id: p.id,
          // Prefer the real id over an arbitrary sequential index -- catalog
          // rows get small integer keys too (`index + 1` in
          // `transformProductList`), so an index-based key here can collide
          // with an unrelated catalog row once it loads, making React reuse/
          // misrender that row's content (a phantom duplicate that doesn't
          // self-correct even after the underlying data becomes correct).
          key: p.id ?? index + 1,
          variety: p.variety,
          manufacturer: p.manufacturer ?? "",
          seed_care: p.seed_care ?? null,
          description: p.description ?? null,
          measure_unit:
            p.measure_unit ?? previous?.measure_unit ?? seeds?.measure_unit,
          density_range: p.density_range ?? previous?.density_range,
          density_options: p.density_options ?? previous?.density_options,
        };
      });

      setSelectedProducts(remapped);
      setSelectedRowKeys(remapped.map((p) => p.key));

      // Same gap as per-product density above: this endpoint doesn't carry
      // the draft's own global density customization, so only override a
      // global field when the backend actually supplied it -- otherwise keep
      // whatever the draft already had (e.g. "To all" with real min/max).
      const resolvedGlobalDensityRange =
        typeof seeds?.global_density_range === "boolean"
          ? seeds.global_density_range
          : globalDensityRange;

      setGlobalDensityRange(resolvedGlobalDensityRange);
      setGlobalDensityValues({
        min: seeds?.density_range?.min ?? globalDensityValues?.min ?? null,
        max: seeds?.density_range?.max ?? globalDensityValues?.max ?? null,
      });
      setMeasureUnitSelection(seeds?.measure_unit ?? measureUnitSelection);
    } else {
      // Nothing survived (no products, or every one mismatched). The catalog
      // for the new country is still fetched below so the browse table isn't
      // left empty -- the user should still be able to pick a product.
      seedCountryChangeClearRef.current = true;
      clearAllSeedSelections();
    }

    const hasOptions =
      (Array.isArray(seeds?.density_options) && seeds.density_options.length) >
        0 ||
      remapped.some(
        (p: any) =>
          Array.isArray(p?.density_options) && p.density_options.length > 0
      );
    // Backend gave no density info of ANY kind (neither range nor options) --
    // keep whatever mode the draft was already in rather than defaulting to
    // "range", for the same reason as the fields above.
    const backendHasDensityInfo =
      seeds?.density_range !== undefined ||
      seeds?.density_options !== undefined;
    const resolvedMode: DensityMode = hasOptions
      ? "options"
      : backendHasDensityInfo
      ? "range"
      : globalDensityMode;
    if (anySurvive) {
      setGlobalDensityMode(resolvedMode);
      setGlobalDensityOptions(
        Array.isArray(seeds?.density_options) &&
          seeds.density_options.length > 0
          ? seeds.density_options
          : resolvedMode === "options"
          ? globalDensityOptions
          : []
      );
    }

    setloading(true);
    // `seedRevalidationProcessing` is also the signal the existingData-restore
    // effect above checks (`revalidationFetchActive`) before firing its OWN
    // catalog fetch for the OLD/persisted crop. Setting it here -- even though
    // we're not using the old name-match pathway -- stops that effect from
    // racing us and clobbering `productList` with the stale-country catalog
    // (confirmed: that race is exactly why the browse table came back empty).
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      countryChangeSeedsPending: false,
      countryChangeSeeds: null,
      seedRevalidationProcessing: true,
      ...(anySurvive
        ? {
            products: remapped,
            measure_unit: seeds?.measure_unit ?? measureUnitSelection,
            density_range: seeds?.density_range ?? {
              min: globalDensityValues?.min ?? null,
              max: globalDensityValues?.max ?? null,
            },
            globalDensityRange:
              seeds?.global_density_range ?? globalDensityRange,
            globalDensityMode: resolvedMode,
            globalDensityOptions: Array.isArray(seeds?.density_options)
              ? seeds.density_options
              : globalDensityOptions,
          }
        : {}),
    }));

    // Fetch the new country's catalog so the browse table shows the
    // available products (pre-selected ones when matched, or simply the full
    // list to pick from when the previous selection was cleared). Row keys
    // get realigned (matched by id) by the catalog-sync effect below once
    // this resolves.
    const applyCatalogFetch = async (): Promise<void> => {
      try {
        await getProductList(
          formValidationData?.revalidationLocalizedCropIds ?? []
        );
      } catch (e) {
        console.error(e);
        // getProductList is the only place that ever clears `loading` (on a
        // successful, non-stale response) -- if it throws before reaching
        // that line, nothing else will, and the table spins forever.
        setloading(false);
      } finally {
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          seedRevalidationProcessing: false,
        }));
      }
    };
    void applyCatalogFetch();
  }, [formValidationData?.countryChangeSeedsPending]);

  // Revalidation result handler (per-product).
  // Runs after the new country's catalog has loaded.
  // - Match each selected product by NAME (variety) against the new catalog.
  // - Every product that still exists is kept, remapping id/key to the new
  //   country's catalog and preserving its own density/rate data untouched.
  // - A product that no longer exists is dropped individually (its density/
  //   rate data goes with it); surviving products are unaffected.
  // - If NOTHING survives, fall back to clearing the whole selection.
  useEffect(() => {
    if (loading) return;
    if (!formValidationData?.seedRevalidationProcessing) return;

    // #2: only proceed once the revalidation fetch we triggered has resolved.
    // Prevents matching against a stale catalog from the initial load.
    if (!revalidationFetchPendingRef.current) return;

    // Wait for this component's own protocol fetch to resolve before judging
    // anything. Defer (without consuming the pending flag) until it has.
    if (protocolId && !existingData) return;

    // Validate against the persisted seed list straight from `existingData`
    // rather than the `selectedProducts` state. Both this effect and the
    // existingData-restore effect below react to the SAME `existingData`
    // change in the SAME commit; `existingData` itself is guaranteed fresh
    // here (it's the dependency that just changed), but `selectedProducts` -
    // set by that OTHER effect via setState - is not visible until the NEXT
    // render. Reading the persisted source directly sidesteps that
    // render-order race entirely, so we never wrongly conclude "nothing
    // selected" on the very render existingData first arrives.
    const productsToValidate: Product[] = protocolId
      ? existingData?.data?.product_details?.seeds?.products ?? []
      : selectedProducts;

    revalidationFetchPendingRef.current = false;

    if (productsToValidate.length === 0) {
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        seedRevalidationProcessing: false,
      }));
      return;
    }

    // Index the new catalog by normalized variety name.
    const byName = new Map<string, Product>(
      formattedProducts.map((p) => [normalizeName(p.variety), p])
    );

    const matchedProducts = productsToValidate.map((selected) => ({
      selected,
      match: byName.get(normalizeName(selected.variety)),
    }));

    const survivors = matchedProducts.filter(({ match }) => Boolean(match));

    if (survivors.length === 0) {
      // Nothing survived (no selected product exists in the new country's
      // catalog by name) -- clear the selection and its density/rate data.
      // Latch the same guard Scenario B uses so the async "restore persisted
      // seeds from the protocol record" effect (below) cannot resurrect the
      // stale GAUDIO/CHAMBO-style selection after we've just cleared it.
      seedCountryChangeClearRef.current = true;
      clearAllSeedSelections();
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        seedRevalidationProcessing: false,
      }));
      return;
    }

    // Keep every product that still matches by name, remapping id/key to the
    // new country's catalog while preserving its own density/rate data
    // untouched. A product that didn't match is dropped individually (its
    // density/rate data goes with it) -- surviving products are unaffected.
    const remapped = survivors.map(({ selected, match }) => ({
      ...selected,
      id: (match as Product).id,
      key: (match as Product).key,
    }));

    // This path replaces the old direct-restore branch (which used to run
    // whenever `existingData` loaded under the template flow), so it must
    // also take over that branch's job of seeding the LOCAL density-mode UI
    // state (global vs. individual toggle, global min/max, measure unit,
    // options-vs-range) from the persisted data. Leaving these at their
    // component defaults meant per-product density/rate inputs rendered
    // blank even though `remapped` itself correctly preserved each
    // product's own `density_range`/`density_options`.
    const seeds = existingData?.data?.product_details?.seeds;

    // Hoisted so the `setFormValidationData` call below can include
    // `globalDensityMode`/`globalDensityOptions`. `isSeedHasValidData()`
    // reads these fields, so omitting them here caused option-based (and
    // global) density selections to be treated as invalid, leaving the
    // Seeds tab permanently "incomplete" even with the UI fully populated.
    let resolvedDensityMode: DensityMode = "range";
    let resolvedDensityOptions: number[] = [];

    if (seeds) {
      setGlobalDensityRange(seeds?.global_density_range);
      setGlobalDensityValues({
        min: seeds?.density_range?.min ?? null,
        max: seeds?.density_range?.max ?? null,
      });
      setMeasureUnitSelection(seeds?.measure_unit);

      const hasOptions =
        (Array.isArray(seeds?.density_options) &&
          seeds.density_options.length) > 0 ||
        remapped.some(
          (p: any) =>
            Array.isArray(p?.density_options) && p.density_options.length > 0
        );
      resolvedDensityMode = hasOptions ? "options" : "range";
      resolvedDensityOptions = Array.isArray(seeds?.density_options)
        ? seeds.density_options
        : [];

      setGlobalDensityMode(resolvedDensityMode);
      setGlobalDensityOptions(resolvedDensityOptions);
    }

    setSelectedProducts(remapped);
    setSelectedRowKeys(remapped.map((p) => p.key));
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      products: remapped,
      measure_unit: seeds?.measure_unit ?? measureUnitSelection,
      density_range: seeds?.density_range,
      globalDensityRange: seeds?.global_density_range,
      globalDensityMode: resolvedDensityMode,
      globalDensityOptions: resolvedDensityOptions,
      seedRevalidationProcessing: false,
    }));
  }, [
    productList,
    loading,
    formValidationData?.seedRevalidationProcessing,
    existingData,
    protocolId,
  ]);

  // #6 (consolidated): single catalog-driven sync effect.
  // Pushes the current selection into the form AND realigns selected product
  // keys / row-selection keys to the active catalog. Previously this logic was
  // split across two separate effects both keyed on `productList`, which could
  // fight each other and cause extra renders. The product-key remap below only
  // updates `selectedProducts` when a key actually changed, so this effect
  // stays stable (no render loop).
  useEffect(() => {
    const seedDetailsForForm: any = {
      global_density_range: globalDensityRange,
      measure_unit: measureUnitSelection,
      products: selectedProducts,
    };

    if (globalDensityMode === "options") {
      seedDetailsForForm.density_range = null;

      seedDetailsForForm.density_options = globalDensityRange
        ? globalDensityOptions
        : null;

      seedDetailsForForm.products = selectedProducts.map((p) => ({
        ...p,
        density_range: undefined,
      }));
    } else {
      seedDetailsForForm.density_options = null;

      seedDetailsForForm.density_range = globalDensityRange
        ? {
            max: globalDensityValues?.max,
            min: globalDensityValues?.min,
          }
        : null;

      seedDetailsForForm.products = selectedProducts.map((p) => ({
        ...p,
        density_options: undefined,
      }));
    }

    form.setFieldsValue({
      seed_details: seedDetailsForForm,
    });

    // Realign keys against the current catalog (merged from the former second
    // effect). Compute matched row keys and detect whether any product key
    // actually changed; only then write back to `selectedProducts`.
    // Match by id first, but fall back to normalized variety name -- a
    // persisted protocol's `product_details.seeds.products[]` never carries a
    // catalog `id` (only variety/manufacturer/density data), so relying on id
    // alone leaves every row on a reopened, non-template edit permanently
    // unmatched (0 selected rows) even though the products are correct.
    const matchedKeys: React.Key[] = [];
    let keysChanged = false;
    const unmatchedProducts: Product[] = [];
    // Normalized varieties that resolved to a real catalog entry this pass --
    // used below to prune any stale, id-less `extraProducts` fallback entry
    // for the SAME product now that a proper catalog match supersedes it.
    // Without this, a product that briefly looked uncatalogued (e.g. before
    // the catalog fetch resolved) and later matched would leave both its
    // fallback (identity = variety string) and its real entry (identity =
    // catalog id) in the pool forever, showing the same product twice.
    const matchedVarieties = new Set<string>();
    const remappedProducts = selectedProducts.map((selected) => {
      const matched =
        formattedProducts.find((p) => p.id && p.id === selected.id) ??
        formattedProducts.find(
          (p) => normalizeName(p.variety) === normalizeName(selected.variety)
        );

      if (matched) {
        matchedKeys.push(matched.key);
        matchedVarieties.add(normalizeName(matched.variety));
        if (matched.key !== selected.key || matched.id !== selected.id) {
          keysChanged = true;
          return { ...selected, id: matched.id, key: matched.key };
        }
        return selected;
      }

      // No catalog entry at all for this product YET (an empty/incomplete
      // catalog fetch, or a product persisted directly onto the protocol
      // with no corresponding browsable catalog row). The row still renders
      // -- `filteredProductsData` splices it in straight from
      // `selectedProducts` regardless of catalog presence -- so it needs a
      // STABLE key of its own rather than being left unable to ever appear
      // in `selectedRowKeys`, which is the reason the checkbox never showed
      // as checked even though the product genuinely is selected.
      //
      // Prefer `id` over any already-assigned `selected.key`: catalog rows
      // get small sequential integer keys (`index + 1` in
      // `transformProductList`), so a product that was momentarily
      // registered with a small-integer fallback key BEFORE the catalog
      // loaded (e.g. `key: 1`) can collide with a genuinely different
      // catalog row that later lands at that same position (also `key: 1`).
      // Two array records sharing one key make React reuse/misrender the
      // wrong row's content -- the exact cause of a phantom duplicate row
      // that visually never self-corrects even after `selectedProducts`
      // itself gets the right data. `id` is a real UUID and can never
      // collide with those small integers.
      const fallbackKey = selected.id ?? selected.key ?? selected.variety;
      matchedKeys.push(fallbackKey);
      const remapped =
        fallbackKey !== selected.key
          ? { ...selected, key: fallbackKey }
          : selected;
      if (fallbackKey !== selected.key) {
        keysChanged = true;
      }
      unmatchedProducts.push(remapped);
      return remapped;
    });

    setSelectedRowKeys(matchedKeys);
    if (keysChanged) {
      setSelectedProducts(remappedProducts);
    }

    // A crop revalidation is deciding THIS render whether `selectedProducts`
    // (still holding the OLD crop's items -- this effect and the revalidation
    // effect both fire off the same `productList` update, from the SAME
    // pre-clear render) survives or gets dropped. Adding those items to the
    // additive/never-auto-pruned `extraProducts` pool here would leak them
    // into the NEW crop's browse list even after the revalidation effect
    // clears `selectedProducts` moments later -- `clearAllSeedSelections`
    // resets `extraProducts` too, but only for what's already accumulated up
    // to that point, not for entries this effect is about to add in the same
    // pass. Skip the additive population entirely while a revalidation is in
    // flight; once it resolves, this effect reruns against the settled
    // selection and catalog.
    const revalidationInFlight = Boolean(
      formValidationData?.seedRevalidationRequired ||
        formValidationData?.seedRevalidationProcessing ||
        formValidationData?.countryChangeSeedsPending
    );

    // Register any uncatalogued product into the stable pool ADDITIVELY
    // (never removed here for a product that's STILL uncatalogued) so
    // deselecting it later still leaves it available -- as an unchecked row
    // -- in the left table instead of vanishing. Do prune an entry whose
    // product just matched the catalog this pass (see `matchedVarieties`
    // above) -- it's superseded by the real catalog row now and keeping both
    // would show the same product twice.
    if (
      !revalidationInFlight &&
      (unmatchedProducts.length > 0 || matchedVarieties.size > 0)
    ) {
      setExtraProducts((prev) => {
        const byIdentity = new Map(prev.map((p) => [productIdentity(p), p]));
        let changed = false;
        prev.forEach((p) => {
          if (matchedVarieties.has(normalizeName(p.variety))) {
            byIdentity.delete(productIdentity(p));
            changed = true;
          }
        });
        unmatchedProducts.forEach((p) => {
          if (!byIdentity.has(productIdentity(p))) {
            byIdentity.set(productIdentity(p), p);
            changed = true;
          }
        });
        return changed ? Array.from(byIdentity.values()) : prev;
      });
    }
  }, [
    selectedProducts,
    globalDensityRange,
    globalDensityMode,
    globalDensityValues,
    globalDensityOptions,
    measureUnitSelection,
    productList,
    formValidationData?.seedRevalidationRequired,
    formValidationData?.seedRevalidationProcessing,
    formValidationData?.countryChangeSeedsPending,
  ]);

  const updateProductDensityRange = (
    productId: string,
    densityType: "max" | "min",
    value: number
  ): void => {
    if (globalDensityRange) {
      setGlobalDensityValues({ ...globalDensityValues, [densityType]: value });
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        density_range: { ...globalDensityValues, [densityType]: value },
        globalDensityRange: true,
        measure_unit: measureUnitSelection,
        globalDensityMode: "range",
      }));
    } else {
      const modifiedProducts = selectedProducts.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            density_range: { ...product.density_range, [densityType]: value },
          };
        }
        return product;
      });

      setSelectedProducts(modifiedProducts);

      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        products: modifiedProducts,
        density_range: null,
        globalDensityRange: false,
        measure_unit: measureUnitSelection,
        globalDensityMode: "range",
      }));
    }
  };

  const updateProductDensityOptions = (
    productId: string,
    values: number[]
  ): void => {
    const modifiedProducts = selectedProducts.map((product) => {
      if (product.id === productId) {
        return { ...product, density_options: values };
      }
      return product;
    });

    setSelectedProducts(modifiedProducts);

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      products: modifiedProducts,
      density_range: null,
      measure_unit: measureUnitSelection,
      globalDensityRange: false,
      globalDensityMode: "options",
    }));
  };

  const updateAllProductsDensityOptions = (values: number[]): void => {
    setGlobalDensityOptions(values);

    const modifiedProducts = selectedProducts.map((product) => ({
      ...product,
      density_options: values,
    }));

    setSelectedProducts(modifiedProducts);

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      products: modifiedProducts,
      density_range: null,
      measure_unit: measureUnitSelection,
      globalDensityRange: true,
      globalDensityMode: "options",
      globalDensityOptions: values,
    }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]): void => {
    setSelectedRowKeys(newSelectedRowKeys);

    // Resolve against the table's actual rendered rows, not the raw catalog.
    // `filteredProductsData` also carries already-selected products spliced in
    // from `selectedProducts` even when the fetched catalog doesn't (yet, or
    // ever) contain them -- filtering `formattedProducts` alone would silently
    // drop any such row from `newSelectedRowKeys`, wiping out an
    // already-selected product the instant ANY checkbox was toggled.
    const tableRows = filteredProductsData ?? formattedProducts;
    const selectedFromTable = tableRows.filter((product: Product) =>
      newSelectedRowKeys.includes(product.key)
    );

    const prevById = new Map<string, Product>(
      selectedProducts.map((p) => [p.id, p])
    );

    const mergedSelected: Product[] = selectedFromTable.map((item) => {
      const prev = prevById.get(item.id);

      const preservedDensityRange = prev?.density_range ?? item.density_range;
      const preservedDensityOptions =
        prev?.density_options ?? item.density_options;

      const base: Product = {
        ...(prev ?? item),
        ...item,
        measure_unit: measureUnitSelection,
      };

      if (globalDensityMode === "options") {
        return {
          ...base,
          density_range: undefined,
          density_options: globalDensityRange
            ? globalDensityOptions
            : preservedDensityOptions,
        };
      }

      return {
        ...base,
        density_options: undefined,
        density_range: globalDensityRange ? undefined : preservedDensityRange,
      };
    });

    setSelectedProducts(mergedSelected);

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      products: mergedSelected,
      measure_unit: measureUnitSelection,
      density_range: globalDensityMode === "range" ? globalDensityValues : null,
      globalDensityRange,
      globalDensityMode,
      globalDensityOptions,
    }));
  };

  const onDeleteProduct = (key: React.Key): void => {
    const indexToDelete = selectedRowKeys.findIndex((x) => x === key);
    const updatedRowKeys = selectedRowKeys.filter(
      (x, index) => index !== indexToDelete
    );
    setSelectedRowKeys(updatedRowKeys);

    const selected = selectedProducts.filter((product: Product) =>
      updatedRowKeys.find((x) => x === product.key)
    );

    setSelectedProducts(selected);

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      products: selected,
      density_range: null,
      measure_unit: measureUnitSelection,
    }));
  };

  const normalizeSortValue = (value: string | null | undefined): string =>
    value ?? "";

  const columns = [
    {
      title: formatTranslation(t("createProtocol.products.tableTitle1")),
      dataIndex: "variety",
      key: "variety",
      sorter: (a: any, b: any) => a.variety.localeCompare(b.variety),
    },
    {
      title: formatTranslation(t("createProtocol.products.tableTitle2")),
      dataIndex: "manufacturer",
      key: "manufacturer",
      sorter: (a: any, b: any) => a.manufacturer.localeCompare(b.manufacturer),
    },
    {
      title: formatTranslation(t("createProtocol.products.tableTitle3")),
      dataIndex: "seed_care",
      key: "seed_care",
      sorter: (a: any, b: any) =>
        normalizeSortValue(a?.seed_care).localeCompare(
          normalizeSortValue(b?.seed_care)
        ),
    },
    {
      title: formatTranslation(
        t("createProtocol.objectives.objectiveItemDescription")
      ),
      dataIndex: "description",
      key: "description",
      sorter: (a: any, b: any) =>
        normalizeSortValue(a?.description).localeCompare(
          normalizeSortValue(b?.description)
        ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const hasSelected = selectedProducts?.length > 0;

  const closeDrawer = (): void => {
    setIsDrawerOpen(false);
  };

  const handleMeasureUnitChange = (value: string): void => {
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      measure_unit: value,
    }));
    setMeasureUnitSelection(value);
    setSelectedProducts(
      selectedProducts?.map((item) => ({
        ...item,
        measure_unit: value,
      }))
    );
  };

  const toggleDensityRange = (e: boolean): void => {
    setGlobalDensityRange(e);

    if (!e) {
      setGlobalDensityValues({ min: null, max: null });
      setGlobalDensityOptions([]);
    }

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      globalDensityRange: e,
      measure_unit: measureUnitSelection,
      products: selectedProducts,
      globalDensityMode,
      globalDensityOptions,
    }));
  };

  const handleCentralDensityModeChange = (e: RadioChangeEvent): void => {
    const mode = e.target.value as DensityMode;
    setGlobalDensityMode(mode);

    if (mode === "range") {
      setGlobalDensityOptions([]);

      const clearedProducts: Product[] = selectedProducts.map((p) => ({
        ...p,
        density_options: undefined,
      }));
      setSelectedProducts(clearedProducts);

      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        globalDensityMode: "range",
        globalDensityOptions: [],
        density_range: globalDensityRange ? globalDensityValues : null,
        globalDensityRange,
        measure_unit: measureUnitSelection,
        products: clearedProducts,
      }));
    } else {
      setGlobalDensityValues({ min: null, max: null });

      const clearedProducts: Product[] = selectedProducts.map((p) => ({
        ...p,
        density_range: undefined,
      }));
      setSelectedProducts(clearedProducts);

      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        globalDensityMode: "options",
        density_range: null,
        globalDensityRange,
        measure_unit: measureUnitSelection,
        globalDensityOptions,
        products: clearedProducts,
      }));
    }
  };

  const [searchText, setSearchText] = useState<string>("");
  const [filteredProductsData, setFilteredProductsData] = useState<Product[]>();
  const { pagination, handleTableChange } = usePagination(
    10,
    filteredProductsData?.length
  );

  const searchData = (): void => {
    const selectedIdentities = new Set(selectedProducts.map(productIdentity));

    // The browsable pool is the catalog PLUS any product that's ever been
    // selected but has no catalog entry (`extraProducts`, additive-only) --
    // otherwise a deselected, uncatalogued product has nowhere to be sourced
    // from and vanishes instead of reappearing as an unchecked row.
    const poolByIdentity = new Map<string, Product>();
    (formattedProducts ?? []).forEach((p) =>
      poolByIdentity.set(productIdentity(p), p)
    );
    extraProducts.forEach((p) => {
      if (!poolByIdentity.has(productIdentity(p))) {
        poolByIdentity.set(productIdentity(p), p);
      }
    });
    const pool = Array.from(poolByIdentity.values());

    const candidates =
      searchText.trim() === ""
        ? pool
        : pool.filter((entry: Product) =>
            entry.variety
              .toLocaleLowerCase()
              .includes(searchText.toLocaleLowerCase())
          );

    const rest = candidates.filter(
      (p: Product) => !selectedIdentities.has(productIdentity(p))
    );

    setFilteredProductsData([...selectedProducts, ...rest]);
  };

  useEffect(() => {
    searchData();
  }, [searchText, productList, selectedProducts, extraProducts]);

  const hideDensityHeader =
    !globalDensityRange && globalDensityMode === "options";

  return (
    <div className="products-seed-component">
      <div className="products-left">
        <h3>{formatTranslation(t("createProtocol.products.title"))}</h3>
        <div className="product-left-content">
          <Search
            className="text-semibold products-search"
            searchText={searchText}
            setSearchTexts={setSearchText}
          />

          {Boolean(formValidationData?.showCountryChangeAlert) && (
            <div className="change-country-alert">
              <div className="change-country-alert__header">
                <WarningOutlined className="change-country-alert__icon" />
                <span className="change-country-alert__title">
                  Change Country Alert
                </span>
              </div>
              <p className="change-country-alert__body">
                Crop and product names vary by country. Changing the country may
                change the selections if an automatic match fails. Please review
                the selections and add missing items manually.
              </p>
            </div>
          )}

          <div>
            <p>
              {hasSelected ? `Selected ${selectedRowKeys?.length} items` : ""}
            </p>

            <PTable
              rowSelection={rowSelection}
              onChange={handleTableChange}
              columns={columns}
              dataSource={filteredProductsData ?? formattedProducts}
              loading={loading}
              pagination={{ ...pagination, showLessItems: true }}
            />
          </div>
        </div>
      </div>
      <div
        className={[
          "products-right",
          !hasSelected ? "empty-product-state" : "",
          !globalDensityRange
            ? "density-mode--individual"
            : "density-mode--all",
          globalDensityMode === "options"
            ? "density-mode--options"
            : "density-mode--range",
        ].join(" ")}
      >
        {!hasSelected ? (
          <div className="drawer-newitem">
            <img src={boxIcon} alt="product empty state icon" />
            <div className="empty-state-pcontent">
              <h3>
                {formatTranslation(
                  t("createProtocol.products.noProductSelected")
                )}
              </h3>
              <p>{formatTranslation(t("createProtocol.products.paragraph"))}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="prodcuts-right-header">
              <h3>
                {formatTranslation(t("createProtocol.products.rightTilte"))}
              </h3>
            </div>

            <div className="drawer-options">
              <div className="drawer-density-measure-unit global-density-range">
                <p>
                  {formatTranslation(
                    t("createProtocol.products.densityRangeApply")
                  )}
                </p>
                <div className="density-switch">
                  <button
                    className={`density-switch-all ${
                      globalDensityRange ? "isActive" : ""
                    }`}
                    onClick={() => {
                      toggleDensityRange(true);
                    }}
                  >
                    {formatTranslation(
                      t("createProtocol.products.densityRangeToAll")
                    )}
                  </button>

                  <button
                    className={
                      "density-switch-individual" +
                      (!globalDensityRange ? " isActive" : "")
                    }
                    onClick={() => {
                      toggleDensityRange(false);
                    }}
                  >
                    {formatTranslation(
                      t("createProtocol.products.densityRangeToIndividual")
                    )}
                  </button>
                </div>
              </div>

              <div className="drawer-density-measure-unit">
                <p>
                  {formatTranslation(t("createProtocol.products.measureUnits"))}
                </p>
                <Select
                  className="measurement-unit-select"
                  size="small"
                  defaultValue={measureUnitSelection}
                  value={measureUnitSelection}
                  onChange={(value: string): void => {
                    handleMeasureUnitChange(value);
                  }}
                  options={measureUnitOptions}
                />
              </div>
            </div>

            <Radio.Group
              value={globalDensityMode}
              onChange={handleCentralDensityModeChange}
              style={{ marginBottom: "16px" }}
            >
              <Space direction="horizontal" size={16}>
                <Radio value="range">By range</Radio>
                <Radio value="options">By options</Radio>
              </Space>
            </Radio.Group>

            {globalDensityRange ? (
              <DensityRangeComponent
                globalDensityRange={globalDensityRange}
                globalDensityValues={globalDensityValues}
                updateProductDensityRange={updateProductDensityRange}
                productId="__ALL__"
                productDensityOptions={globalDensityOptions}
                updateProductDensityOptions={(
                  _productId: string,
                  values: number[]
                ) => {
                  updateAllProductsDensityOptions(values);
                }}
                densityMode={globalDensityMode}
              />
            ) : (
              <></>
            )}

            <div className="product-card-container">
              <div className="product-card-heading">
                <p className="seed-product-name">
                  {formatTranslation(t("createProtocol.products.rightTilte"))}
                </p>

                {!hideDensityHeader ? (
                  <p className="seed-product-range">
                    {formatTranslation(
                      t("createProtocol.products.densityRange")
                    )}{" "}
                    in {measureUnitSelection}
                  </p>
                ) : null}
              </div>

              {[...selectedProducts]
                .sort((a, b) => a.variety.localeCompare(b.variety))
                .map((product) => (
                  <Flex vertical key={product.id}>
                    <ProductCard
                      key={product.key}
                      id={product.id}
                      updateProductDensityRange={updateProductDensityRange}
                      updateProductDensityOptions={updateProductDensityOptions}
                      onDeleteProduct={onDeleteProduct}
                      productKey={product.key}
                      globalDensityRange={globalDensityRange}
                      globalDensityMode={globalDensityMode}
                      globalDensityValues={globalDensityValues}
                      variety={product.variety}
                      manufacturer={product.manufacturer}
                      measureUnit={product.measure_unit}
                      productDensityRangeMin={product.density_range?.min}
                      productDensityRangeMax={product.density_range?.max}
                      productDensityOptions={product.density_options ?? []}
                    />
                  </Flex>
                ))}
            </div>
          </>
        )}

        <Drawer
          className="objective-drawer"
          rootClassName="objective-drawer"
          title="Objective information"
          onClose={closeDrawer}
          open={isDrawerOpen}
        >
          <h2 className="objective-drawer-name">Product Details</h2>
          <div>
            <h3>SY KingsBarn</h3>
            <br />
            <div>Variety Overview</div>
            <div>
              <strong>End-use group: </strong> Six-row feed, Hybrid
            </div>
            <div>
              <strong>Market options: </strong> Livestock Feed
            </div>
          </div>
        </Drawer>
      </div>
    </div>
  );
};

export default ProductsSeedComponent;
